import { useEffect, useRef } from 'react';
import { type UseFormReturn, useFormState } from 'react-hook-form';
import { hasDirtyFields } from '../flattenDirtyFields.ts';
import { useFormCoordinatorContext } from './FormCoordinatorContext.tsx';

interface ICoordinatedFormInput {
  id: string | null | undefined;
  methods: UseFormReturn;
}

/**
 * Registra il form all'interno del FormCoordinator e ne propaga lo stato
 * (`isDirty`, `isValid`) tramite `registerForm`.
 *
 * Il coordinator mantiene lo stato in un ref (non in useState) per evitare
 * che ogni aggiornamento cambi il context value e causi
 * `propagateContextChange_eager` (~40ms con 200+ widget).
 *
 * L'effect di registrazione gestisce sia la registrazione/deregistrazione
 * sia l'aggiornamento dello stato. La deregistrazione del vecchio id al
 * cambio di `form.id` avviene nel body dell'effect (non nel cleanup) così
 * che i cambi di isDirty/isValid non causino unregister → flicker.
 * Un effect separato gestisce la deregistrazione all'unmount.
 */
export function useRegisterCoordinatedForm(form: ICoordinatedFormInput) {
  const { registerForm, unregisterForm } = useFormCoordinatorContext();

  const methodsRef = useRef(form.methods);
  methodsRef.current = form.methods;

  // Non possiamo usare `methods.formState` direttamente perché non
  // costringerebbe l'intero form (_ogni_ campo) al re-rendering.
  //
  // NOTA: NON sottoscrivere `isValid` da useFormState! Il proxy di RHF
  // marca la proprietà come "subscribed", forzando `_setValid → _runSchema`
  // (full resolver) su ogni `register` di ogni campo. Con 50+ campi questo
  // aggiunge ~500ms al mount. Deriviamo `isValid` da `errors` che è già
  // sottoscritto da ogni useField, senza overhead aggiuntivo.
  //
  // Usiamo un singolo useFormState per entrambi: `dirtyFields` serve per
  // calcolare isDirty (vedi hasDirtyFields), `errors` per calcolare isValid.
  const { dirtyFields, errors } = useFormState();
  const isDirty = hasDirtyFields(dirtyFields);
  const isValid = !errors || Object.keys(errors).length === 0;

  // Ultimo id effettivamente registrato. Aggiornato solo dentro l'effect
  // così da catturare il valore precedente e poter deregistrare il vecchio
  // form quando form.id cambia.
  const registeredIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (
      registeredIdRef.current &&
      registeredIdRef.current !== form.id &&
      unregisterForm
    ) {
      unregisterForm(registeredIdRef.current);
    }
    registeredIdRef.current = form.id;

    if (!form.id || !registerForm) return;
    registerForm(methodsRef, { id: form.id, isDirty, isValid });
  }, [form.id, registerForm, isDirty, isValid, unregisterForm]);

  // Cleanup separato: deregistra solo all'unmount.
  useEffect(() => {
    return () => {
      if (registeredIdRef.current && unregisterForm) {
        unregisterForm(registeredIdRef.current);
      }
    };
  }, [unregisterForm]);
}
